/**
 * ChatComposer — the one chat message composer.
 *
 * The AI Assistant (`ChatInput`) and Tender Chat (`TenderChat`) ran ~90%
 * identical hand forks of this component. Everything that actually differed
 * between them is a prop: the store callbacks, the placeholder, the draft-stash
 * key, the read-only gate, the scope control slot, and the assistant-only
 * quoted-passage chip.
 *
 * Callers must keep this component at ONE stable tree position across any
 * empty↔has-messages layout flip: it holds the local draft and runs a one-shot
 * SharePoint-stash restore on mount, both of which a remount silently loses.
 */

import { useState, useRef, useEffect } from "react"
import { ArrowUp, Loader2, BookOpen, Save, Square, X } from "lucide-react"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { PromptLibraryDialog } from "@/components/assistant/PromptLibraryDialog"
import { SavePromptDialog } from "@/components/assistant/SavePromptDialog"
import { ChatAttachments } from "@/components/assistant/ChatAttachments"
import { AttachmentButton } from "@/components/assistant/AttachmentButton"
import { useAssistantAttachments } from "@/hooks/use-assistant-attachments"
import {
  CHAT_ACCEPTED_FILE_TYPES,
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_TOTAL_SIZE,
} from "@/lib/assistant-attachment-validation"
import { consumeDraftStash } from "@/lib/draft-stash"
import { handleUnwrappingPaste } from "@/lib/unwrap-pasted-text"
import { cn } from "@/lib/utils"

interface ChatComposerProps {
  sendMessage: (
    content: string,
    attachments?: Array<{
      attachmentId: string
      s3Key: string
      filename: string
      mimeType: string
      size: number
    }>
  ) => Promise<boolean>
  cancelStream: () => void
  isLoading: boolean
  isStreaming: boolean
  /** Accessible name for the textarea. */
  label: string
  /** Hint shown in the empty box — short, 14px, `text-subtle`. */
  placeholder: string
  draftStashKey: string
  readOnly?: boolean
  /** Measure the textarea only when its host conversation is visible. */
  isVisible?: boolean
  /** Fill the host column instead of centring a max-w-3xl box (Home). */
  fullWidth?: boolean
  /** Rendered in the controls row after the attach button (Assistant: <ContextScopeMenu />). */
  scopeControl?: React.ReactNode
  /** Assistant-only quoted-passage chip. */
  panelSelection?: { text: string } | null
  onClearPanelSelection?: () => void
  /** Placeholder override while a selection is active. */
  selectionPlaceholder?: string
  /**
   * The input box's fill. `muted` stands out on a white workspace (default);
   * `card` stands out on a tinted panel such as the assistant dock.
   */
  inputSurface?: 'muted' | 'card'
}

export function ChatComposer({
  sendMessage,
  cancelStream,
  isLoading,
  isStreaming,
  label,
  placeholder,
  draftStashKey,
  readOnly = false,
  isVisible = true,
  fullWidth = false,
  scopeControl,
  panelSelection = null,
  onClearPanelSelection,
  selectionPlaceholder,
  inputSurface = 'muted',
}: ChatComposerProps) {
  const [input, setInput] = useState("")
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false)
  const [isSavePromptOpen, setIsSavePromptOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const {
    attachments,
    addFiles,
    addSharePointItems,
    removeAttachment,
    clearAttachments,
    getReadyRefs,
    isUploading,
    hasErrors,
    canAttach,
    totalSize,
  } = useAssistantAttachments()

  const isDisabled = readOnly || isLoading || isStreaming

  // Restore a draft stashed before the SharePoint connect redirect. Consume is
  // one-shot, and an already-typed message always wins over the stash.
  useEffect(() => {
    const stashed = consumeDraftStash(draftStashKey)
    if (stashed) setInput((prev) => prev || stashed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Starts at one row and grows with the text, up to 200px.
  useEffect(() => {
    if (isVisible && textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [input, isVisible])

  const handleSend = async () => {
    if (!input.trim() || isDisabled || isUploading || hasErrors) return

    const refs = getReadyRefs()
    const dispatched = await sendMessage(input.trim(), refs.length > 0 ? refs : undefined)
    // A not-ready session never sent anything — keep the draft and the chips so
    // the user can retry instead of retyping.
    if (!dispatched) return
    setInput("")
    clearAttachments()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleSelectPrompt = (promptContent: string) => {
    setInput(promptContent)
    setIsPromptLibraryOpen(false)
    textareaRef.current?.focus()
  }

  // Dropzone for drag-and-drop
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    accept: CHAT_ACCEPTED_FILE_TYPES,
    disabled: !canAttach || isDisabled,
  })

  return (
    <>
      <div className="px-4 pt-3 pb-6">
        <div className={cn(!fullWidth && "max-w-3xl mx-auto")}>
          {/* Unified Input Box */}
          <div
            {...getRootProps()}
            className={cn(
              "border rounded-2xl transition-colors focus-within:ring-[3px] focus-within:ring-[hsl(var(--brand)/0.16)] focus-within:border-[hsl(var(--brand))]",
              inputSurface === 'card' ? "bg-card" : "bg-muted",
              isDragActive
                ? "ring-2 ring-[hsl(var(--brand))] border-[hsl(var(--brand))]"
                : "border-[hsl(var(--border))]"
            )}
          >
            {/* react-dropzone renders this input for the file-dialog path. With
                `noClick` nothing here opens it, but it still rendered visible at
                1x1px and so appeared in the accessibility tree as a stray,
                unlabelled "Choose File" control immediately before the message
                box. Hidden from assistive tech and taken out of the tab order;
                the element itself stays so dropzone's internals are untouched. */}
            <input {...getInputProps()} aria-hidden="true" tabIndex={-1} />

            {/* Attachment chips */}
            <ChatAttachments
              attachments={attachments}
              onRemove={removeAttachment}
            />

            {/* Passage highlighted in the document panel — sent as a quote with
                the next message, so show it and let the user drop it */}
            {panelSelection && (
              <div className="flex items-center gap-1.5 px-3 pt-2">
                <span className="text-xs italic text-muted-foreground truncate max-w-[420px]">
                  &ldquo;
                  {panelSelection.text.length > 60
                    ? panelSelection.text.slice(0, 60) + '…'
                    : panelSelection.text}
                  &rdquo;
                </span>
                <button
                  aria-label="Clear selection"
                  onClick={() => onClearPanelSelection?.()}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Textarea — 15px, the size the text becomes once sent. Both
                consumers (Assistant, tender chat) render their turns at 15px;
                at text-sm the words visibly grew on submit. The AI panel has
                its own composer and stays 14px. The hint is 14px in the
                faintest text grey; the toolbar below is 13px. */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handleUnwrappingPaste}
              aria-label={label}
              placeholder={
                panelSelection ? (selectionPlaceholder ?? placeholder) : placeholder
              }
              disabled={isDisabled}
              className="block w-full max-h-[200px] resize-none bg-transparent px-3 pt-3 pb-2 text-[15px] placeholder:text-[14px] placeholder:text-subtle focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              rows={1}
            />

            {/* Bottom Row: Controls + Send */}
            <div className="flex items-center gap-1.5 px-2 pb-2">
              {/* Attach Files */}
              <AttachmentButton
                onFiles={addFiles}
                onSharePointItems={addSharePointItems}
                remainingSlots={MAX_CHAT_ATTACHMENTS - attachments.length}
                remainingBytes={MAX_CHAT_TOTAL_SIZE - totalSize}
                draftStashKey={draftStashKey}
                draftText={input}
                disabled={!canAttach || isDisabled}
                active={attachments.length > 0}
              />

              {/* Context scope */}
              {scopeControl}

              {/* Prompts */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[13px] text-muted-foreground hover:text-foreground"
                onClick={() => setIsPromptLibraryOpen(true)}
                disabled={readOnly}
                aria-label="Prompts"
              >
                {/* Icon-only on phones so the send button keeps its room. */}
                <BookOpen className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden sm:inline">Prompts</span>
              </Button>

              {/* Save as Prompt - appears when user has typed content */}
              {input.trim().length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[13px] text-muted-foreground hover:text-foreground"
                  onClick={() => setIsSavePromptOpen(true)}
                  disabled={readOnly}
                  aria-label="Save"
                >
                  <Save className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Save</span>
                </Button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Send / Stop Button */}
              {isStreaming ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 rounded-full bg-muted text-muted-foreground hover:bg-muted/80"
                  onClick={() => cancelStream()}
                  aria-label="Stop"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                </Button>
              ) : (
                // Primary (black), like every other send button (Kapil, 2 Oct:
                // "assistant send buttons are black too").
                <Button
                  size="icon"
                  variant="default"
                  data-variant={isLoading || isUploading || hasErrors ? "busy" : "default"}
                  aria-label="Send message"
                  className={
                    isLoading || isUploading || hasErrors
                      ? "h-8 w-8 shrink-0 rounded-full bg-muted text-muted-foreground hover:bg-muted"
                      : "h-8 w-8 shrink-0 rounded-full"
                  }
                  disabled={!input.trim() || isLoading || isUploading || hasErrors}
                  onClick={() => void handleSend()}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUp className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <PromptLibraryDialog
        open={isPromptLibraryOpen}
        onOpenChange={setIsPromptLibraryOpen}
        onSelectPrompt={handleSelectPrompt}
      />

      <SavePromptDialog
        open={isSavePromptOpen}
        onOpenChange={setIsSavePromptOpen}
        initialContent={input}
      />
    </>
  )
}
